import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, LucideIcon } from 'lucide-react';
import { Card } from '../../ui/Card';
import { cn } from '../../ui/Button';

interface ListItemAction {
  label: string;
  icon: LucideIcon;
  tone: string;
  onClick: () => void;
}

interface ListItemMetric {
  icon: LucideIcon;
  label: string;
  tone: string;
}

interface ListItemCardProps {
  title: string;
  subtitle?: React.ReactNode;
  badges?: Array<{
    label: string;
    className: string;
  }>;
  leadingIcon: LucideIcon;
  leadingTone?: string;
  leadingVisual?: React.ReactNode;
  metricLeft?: ListItemMetric;
  metricRight?: ListItemMetric;
  menuOpen: boolean;
  onToggleMenu: () => void;
  actions: ListItemAction[];
  onClick?: () => void;
  isActive?: boolean;
}

const MENU_WIDTH = 180;
const MENU_ITEM_HEIGHT = 44;
const MENU_PADDING = 16;
const VIEWPORT_MARGIN = 12;

const ListItemCard: React.FC<ListItemCardProps> = ({
  title,
  subtitle,
  badges,
  leadingIcon: LeadingIcon,
  leadingTone = 'from-primary-light to-primary-light/60 text-primary',
  leadingVisual,
  metricLeft,
  metricRight,
  menuOpen,
  onToggleMenu,
  actions,
  onClick,
  isActive = false,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const computePosition = () => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const estimatedHeight = actions.length * MENU_ITEM_HEIGHT + MENU_PADDING;
    const spaceBelow = window.innerHeight - rect.bottom;
    const shouldRenderAbove = spaceBelow < estimatedHeight + VIEWPORT_MARGIN;
    const top = shouldRenderAbove ? rect.top - estimatedHeight - 8 : rect.bottom + 8;
    const preferredLeft = rect.right - MENU_WIDTH;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(preferredLeft, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN));
    setMenuPosition({ top, left });
  };

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPosition(null);
      return;
    }

    computePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;

    const handleReflow = () => computePosition();
    window.addEventListener('resize', handleReflow);
    window.addEventListener('scroll', handleReflow, true);

    return () => {
      window.removeEventListener('resize', handleReflow);
      window.removeEventListener('scroll', handleReflow, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen]);

  return (
    <Card
      onClick={onClick}
      className={cn(
        'overflow-visible border-slate-200 bg-white p-0 shadow-[0_14px_35px_-18px_rgba(15,23,42,0.28)]',
        onClick && 'cursor-pointer transition-all hover:-translate-y-0.5 hover:border-slate-300',
        isActive && 'border-primary ring-2 ring-primary/10'
      )}
    >
      <div className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            {leadingVisual ?? (
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br',
                  leadingTone
                )}
              >
                <LeadingIcon size={17} />
              </div>
            )}

            <div>
              <h4 className="text-base font-bold text-slate-900">{title}</h4>
              {subtitle && <p className="mt-1 text-xs font-medium text-gray-500">{subtitle}</p>}
              {badges && badges.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {badges.map((badge) => (
                    <span
                      key={`${title}-${badge.label}`}
                      className={cn('rounded-full px-2.5 py-1 text-[11px] font-semibold', badge.className)}
                    >
                      {badge.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {actions.length > 0 && (
            <button
              ref={buttonRef}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleMenu();
              }}
              className="rounded-full border border-slate-200 bg-white p-2 text-gray-400 shadow-sm transition-colors hover:bg-slate-50"
            >
              <MoreVertical size={16} />
            </button>
          )}
        </div>

        {(metricLeft || metricRight) && (
          <div className="flex items-center justify-between px-1 py-1">
            {metricLeft ? (
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                <span className={cn('inline-flex rounded-lg p-1.5', metricLeft.tone)}>
                  <metricLeft.icon size={14} />
                </span>
                {metricLeft.label}
              </div>
            ) : (
              <span />
            )}

            {metricRight && (
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                <span className={cn('inline-flex rounded-lg p-1.5', metricRight.tone)}>
                  <metricRight.icon size={14} />
                </span>
                {metricRight.label}
              </div>
            )}
          </div>
        )}
      </div>

      {menuOpen && menuPosition &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[85]"
              onClick={(event) => {
                event.stopPropagation();
                onToggleMenu();
              }}
            />
            <div
              className="fixed z-[86] w-[180px] rounded-2xl border border-slate-200 bg-white p-2 shadow-xl"
              style={{ top: menuPosition.top, left: menuPosition.left }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="space-y-1">
                {actions.map(({ label, icon: Icon, tone, onClick: onActionClick }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onActionClick();
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm font-medium text-slate-700 hover:bg-gray-50"
                  >
                    <span className={cn('inline-flex rounded-lg p-1.5', tone)}>
                      <Icon size={14} />
                    </span>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </Card>
  );
};

export default ListItemCard;
